import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { EChartOption, ECharts } from 'echarts';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CommonService } from '../../service/common.service';
import { BasicScatterService } from './basic-scatter.service';
import { ChartOptionService } from '../../service/chart-option.service';
import { ChartResizeService } from '../../service/chart-resize.service';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'bcac-basic-scatter',
  templateUrl: './basic-scatter.component.html',
  styleUrls: ['./basic-scatter.component.scss']
})
export class BasicScatterComponent implements OnInit {

  constructor(
    private commonService: CommonService,
    private basicScatterService: BasicScatterService,
    private chartOptionService: ChartOptionService,
    private chartResizeService: ChartResizeService
  ) { }

  chartTitle = {
    show: true,
    label: "图表标题"
  };

  customToolbox = {
    show: true
  }

  // 布局设置
  cardPadding = "1rem";
  cardBgColor = "#fff";
  chartTitleColor = "";

  // echart图表元素
  chartBackgroundColor: string = "#FFF";
  chartColors: string[] = this.chartOptionService.getChartColors('default');
  chartTitles = this.chartOptionService.getChartTitle();
  chartLegend = this.chartOptionService.getChartLegend();
  chartGrid: EChartOption.Grid = this.commonService.changeFontSize().interval;
  chartxAxis: EChartOption.XAxis[] = this.chartOptionService.getChartxAxisValue(1);
  chartyAxis: EChartOption.YAxis[] = this.chartOptionService.getChartyAxis(1);
  chartTextStyle:EChartOption.TextStyle = this.chartOptionService.getTextStyle();
  scatterSeries: EChartOption.SeriesScatter = this.basicScatterService.getDefaultScatterSeries();
  chartTooltip: EChartOption.Tooltip = this.basicScatterService.getDefaultScatterTooltip();
  chartSeriesList: EChartOption.Series[] = [];

  chartOption = {
    backgroundColor: this.chartBackgroundColor,
    title: this.chartTitles,
    legend: this.chartLegend,
    grid: this.chartGrid,
    xAxis: this.chartxAxis,
    yAxis: this.chartyAxis,
    series: this.chartSeriesList,
    tooltip: this.chartTooltip,
    color: this.chartColors,
    textStyle: this.chartTextStyle
  };

  initOpts = this.chartOptionService.getInitOpts();
  optionInit: ECharts;
  onOptionInit(ec) {
    this.optionInit = ec;
  }

  // X轴刻度间隔
  intervalData = [];
  intervalSelectedData;
  intervalSelectedDataChange(e: any) {
    // 跟新图表interval
    this.basicScatterService.setInterval(this.chartOption, e);
    this.updateOption();
  }

  axisScaleChange(e: any) {
    this.basicScatterService.setInterval(this.chartOption, e);
    this.updateOption();
  }

  // 接口区
  @Input()
  set bcacDarkMode(darkMode: boolean) {
    if (darkMode == true) {
      this.basicScatterService.changeChartTitleDark(this, darkMode);
      this.basicScatterService.chartBackgroundColorDarkMode(this.chartOption);
      this.basicScatterService.xAxisDarkMode(this.chartxAxis);
      this.basicScatterService.yAxisDarkMode(this.chartyAxis);
      this.basicScatterService.colorsDarkMode(this.chartColors);
      this.basicScatterService.legendDarkMode(this.chartLegend);
      this.basicScatterService.seriesDarkMode(this.scatterSeries);
    }
  }

  @Input()
  set bcacSmallMode(smallMode: boolean) {
    if (smallMode) {
      this.basicScatterService.setSmallModeGrid(this.chartGrid);
      this.basicScatterService.setSmallModePadding(this)
    }
  }

  legendPosition = "bottom";
  @Input()
  set bcacLegendPosition(legendPosition: string) {
    this.legendPosition = legendPosition;
    this.basicScatterService.setLegendPosition(this.chartGrid, this.chartLegend, legendPosition);
  }

  @Input()
  set bcacTitleSwitch(titleSwitch: boolean) {
    this.chartTitle.show = titleSwitch;
  }

  @Input()
  set bcacTitle(title: string) {
    this.chartTitle.label = title;
  }

  @Input()
  set bcacUnit(unit: string[]) {
    this.basicScatterService.setTooltipUnit(this.chartTooltip, unit);
    this.updateOption();
  }

  @Input()
  set bcacXaxisName(xAxisName: string) {
    this.basicScatterService.setXaxisName(this.chartxAxis, xAxisName);
  }

  @Input()
  set bcacYaxisName(yAxisNames: string) {
    this.basicScatterService.setYaxisName(this.chartyAxis, yAxisNames);
  }

  @Input()
  set bcacYnameGap(yNameGap: number) {
    this.basicScatterService.setYNameGap(this.chartyAxis, yNameGap);
  }

  @Input()
  set bcacColors(colorList: string[]) {
    this.basicScatterService.chartColorsChange(this.chartColors, colorList)
  }

  @Input()
  set bcacIntervalData(value: any[]) {
    this.intervalData = value;
  }

  // 加载等待
  _bcacChartLoading: boolean = false
  @Input()
  set bcacChartLoading(loading: boolean) {
    this._bcacChartLoading = loading;
  }

  // 系列的数据维度名
  @Input()
  set bcacDimensions(dimensions: string[]) {
    this.basicScatterService.setDimensions(dimensions, this.scatterSeries)
  }

  @Input()
  set bcacSymbolSize(size: number) {
    this.basicScatterService.setSymbolSize(size, this.scatterSeries)
  }

  // 数据
  legendDataList;
  @Input()
  set bcacChartData(chartData: any) {

    let legendDataList = [];
    if (chartData && chartData.hasOwnProperty('seriesName')) {
      chartData.seriesName.forEach(element => {
        legendDataList.push(
          {
            label: element,
            value: element,
            checked: true
          }
        )
      });
    }
    this.legendDataList = legendDataList;
    this.basicScatterService.buildSeries(chartData, this.chartSeriesList, this.scatterSeries)

    this.updateOption();
  }

  // x轴label旋转
  @Input()
  set bcacXAxisLabelRotate(xAxisLabelRotate) {
    this.basicScatterService.setXAxisLabelRotate(this.chartxAxis, xAxisLabelRotate)
  }

  dataSourceSelectData = [];
  @Input()
  set bcacDataSourceList(value: any[]) {
    this.dataSourceSelectData = value;
  }

  legendChange($event) {
    this.basicScatterService.topLegendChange($event, this.optionInit);
  }

  @Input()
  set bcacYAxisMin(yAxisMin: number | undefined | null) {
    this.basicScatterService.setYAxisMin(this.chartyAxis, yAxisMin);
  }

  @Input()
  set bcacYAxisMax(yAxisMax: number | undefined | null) {
    this.basicScatterService.setYAxisMax(this.chartyAxis, yAxisMax);
  }

  @Input()
  set bcacYAxisInterval(yAxisInterval: number) {
    this.basicScatterService.setYAxisInterval(this.chartyAxis, yAxisInterval)
  }

  @Input()
  set bcacSplitLineShow(splitLineShow: boolean | boolean[]) {
    this.basicScatterService.setSplitLineShow(this.chartxAxis, this.chartyAxis, splitLineShow)
  }

  @Output()
  bcacDataSourceSelectEmit = new EventEmitter<any>();
  dataSourceChange(e: any) {
    this.bcacDataSourceSelectEmit.emit(e);
  }
  options;
  // echart配置项
  initEchart() {
    this.options = this.chartOption;
  }

  updateOption() {
    if (this.optionInit) {
      this.optionInit.setOption(this.options, true)
    }
  }

  ngOnInit() {
    this.initEchart();
    this.updateOption();
    fromEvent(window, 'resize').pipe(debounceTime(200)).subscribe(() => {
      this.chartResizeService.autoResizeAdapt(this.optionInit, this.options)
    })

    // try {
    //   if(localStorage.sourceSansPro) {
    //     this.addFont();
    //   } else {
    //     var request = new XMLHttpRequest();
    //     request.open('GET', '/path/to/source-sans-pro.woff.css', true);

    //     request.onload = ()=> {
    //         if (request.status >= 200 && request.status < 400) {
    //             // 保存到localStorage中，key=sourceSansPro
    //             localStorage.sourceSansPro = request.responseText;

    //             this.addFont();
    //         }
    //     }

    //     request.send();

    //   }
    // } catch (error) {
      
    // }

  }
  addFont() {
    var style = document.createElement('style');
    document.head.appendChild(style);
    style.textContent = localStorage.sourceSansPro;
  }
    
  
  

}
